import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../api';
import type { RatedPlayer, StatOverride, StatOverrideInput, Team } from '../types';
import { SourceBadge } from './SourceBadge';

type Draft = StatOverrideInput;

const EMPTY: Draft = {
  singleWon: 0,
  singleLost: 0,
  singleLegsWon: 0,
  singleLegsLost: 0,
  doubleWon: 0,
  doubleLost: 0,
  doubleLegsWon: 0,
  doubleLegsLost: 0,
  average: null,
  count180: 0,
  count140: 0,
  count100: 0,
  count80: 0,
  note: null,
};

/**
 * A derived average carries full float precision (63.10762331838565), which is
 * unusable in an editable field — and the column stores two decimals anyway.
 */
const round2 = (value: number | null): number | null =>
  value === null ? null : Math.round(value * 100) / 100;

/** The figures currently shown for a player, as an editable starting point. */
const draftFromPlayer = (player: RatedPlayer): Draft => ({
  singleWon: player.singleWon,
  singleLost: player.singleLost,
  singleLegsWon: player.singleLegsWon,
  singleLegsLost: player.singleLegsLost,
  doubleWon: player.doubleWon,
  doubleLost: player.doubleLost,
  doubleLegsWon: player.doubleLegsWon,
  doubleLegsLost: player.doubleLegsLost,
  average: round2(player.average),
  count180: player.count180,
  count140: player.count140,
  count100: player.count100,
  count80: player.count80,
  note: player.overrideNote,
});

const draftFromOverride = (o: StatOverride): Draft => ({
  singleWon: o.singleWon,
  singleLost: o.singleLost,
  singleLegsWon: o.singleLegsWon,
  singleLegsLost: o.singleLegsLost,
  doubleWon: o.doubleWon,
  doubleLost: o.doubleLost,
  doubleLegsWon: o.doubleLegsWon,
  doubleLegsLost: o.doubleLegsLost,
  average: o.average,
  count180: o.count180,
  count140: o.count140,
  count100: o.count100,
  count80: o.count80,
  note: o.note,
});

const same = (a: Draft, b: Draft): boolean => JSON.stringify(a) === JSON.stringify(b);

type NumericField = Exclude<keyof Draft, 'note' | 'average'>;

const NUMBER_COLUMNS: Array<{ key: NumericField; label: string; group: string }> = [
  { key: 'singleWon', label: 'W', group: 'Singles' },
  { key: 'singleLost', label: 'L', group: 'Singles' },
  { key: 'singleLegsWon', label: 'Legs W', group: 'Singles' },
  { key: 'singleLegsLost', label: 'Legs L', group: 'Singles' },
  { key: 'doubleWon', label: 'W', group: 'Doubles' },
  { key: 'doubleLost', label: 'L', group: 'Doubles' },
  { key: 'doubleLegsWon', label: 'Legs W', group: 'Doubles' },
  { key: 'doubleLegsLost', label: 'Legs L', group: 'Doubles' },
  { key: 'count180', label: '180', group: 'Scores' },
  { key: 'count140', label: '140+', group: 'Scores' },
  { key: 'count100', label: '100+', group: 'Scores' },
  { key: 'count80', label: '80+', group: 'Scores' },
];

/**
 * Hand-entered statistics, one row per player.
 *
 * Saved figures live in their own table and are layered over imported data when
 * a player is rated — importing the league again never overwrites them, and the
 * Statistics / Player results tabs keep showing what the source published.
 */
export function ManualStatsPanel({ leagueId }: { leagueId: number }) {
  const [players, setPlayers] = useState<RatedPlayer[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [saved, setSaved] = useState<Map<number, Draft>>(new Map());
  const [drafts, setDrafts] = useState<Map<number, Draft>>(new Map());
  const [teamFilter, setTeamFilter] = useState<number | 'all'>('all');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [p, t, overrides] = await Promise.all([
        api.players(leagueId),
        api.teams(leagueId),
        api.manualStats(leagueId),
      ]);
      const savedMap = new Map(overrides.map((o) => [o.playerId, draftFromOverride(o)]));
      setPlayers(p);
      setTeams(t);
      setSaved(savedMap);
      setDrafts(new Map(p.map((pl) => [pl.playerId, savedMap.get(pl.playerId) ?? draftFromPlayer(pl)])));
      setMessage(null);
    } catch (error) {
      setMessage({ kind: 'error', text: error instanceof ApiError ? error.message : String(error) });
    } finally {
      setBusy(false);
    }
  }, [leagueId]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(
    () => (teamFilter === 'all' ? players : players.filter((p) => p.teamId === teamFilter)),
    [players, teamFilter],
  );

  const baselineOf = (player: RatedPlayer): Draft =>
    saved.get(player.playerId) ?? draftFromPlayer(player);

  const isDirty = (player: RatedPlayer): boolean => {
    const draft = drafts.get(player.playerId);
    return draft !== undefined && !same(draft, baselineOf(player));
  };

  const dirtyPlayers = visible.filter(isDirty);

  const patch = (playerId: number, change: Partial<Draft>) =>
    setDrafts((current) => {
      const next = new Map(current);
      next.set(playerId, { ...(next.get(playerId) ?? EMPTY), ...change });
      return next;
    });

  const saveOne = async (playerId: number) => {
    const draft = drafts.get(playerId);
    if (!draft) return;
    setBusy(true);
    try {
      await api.saveManualStats(leagueId, playerId, draft);
      await load();
      setMessage({ kind: 'ok', text: 'Saved.' });
    } catch (error) {
      setMessage({ kind: 'error', text: error instanceof ApiError ? error.message : String(error) });
      setBusy(false);
    }
  };

  const saveAll = async () => {
    if (dirtyPlayers.length === 0) return;
    setBusy(true);
    try {
      for (const player of dirtyPlayers) {
        const draft = drafts.get(player.playerId);
        if (draft) await api.saveManualStats(leagueId, player.playerId, draft);
      }
      await load();
      setMessage({ kind: 'ok', text: `Saved ${dirtyPlayers.length} players.` });
    } catch (error) {
      setMessage({ kind: 'error', text: error instanceof ApiError ? error.message : String(error) });
      setBusy(false);
    }
  };

  const clearOne = async (playerId: number) => {
    setBusy(true);
    try {
      await api.clearManualStats(leagueId, playerId);
      await load();
      setMessage({ kind: 'ok', text: 'Hand-entered figures discarded.' });
    } catch (error) {
      setMessage({ kind: 'error', text: error instanceof ApiError ? error.message : String(error) });
      setBusy(false);
    }
  };

  /** Loads last season's numbers into the form without saving them. */
  const prefillFromLastSeason = async () => {
    setBusy(true);
    try {
      const rows = await api.manualStatsPrefill(leagueId);
      const wanted = new Set(visible.map((p) => p.playerId));
      const applicable = rows.filter((r) => wanted.has(r.playerId));
      setDrafts((current) => {
        const next = new Map(current);
        for (const row of applicable) {
          const { playerId, displayName: _name, carriedFrom: _from, ...draft } = row;
          next.set(playerId, draft);
        }
        return next;
      });
      setMessage(
        applicable.length === 0
          ? { kind: 'error', text: 'No previous-season figures found for these players.' }
          : {
              kind: 'ok',
              text: `Filled ${applicable.length} players from their previous season. Review, then save.`,
            },
      );
    } catch (error) {
      setMessage({ kind: 'error', text: error instanceof ApiError ? error.message : String(error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <header>
        <h2>Manual statistics</h2>
        <p className="hint">
          Hand-entered figures override imported data when players are rated. They are stored
          separately, so re-importing the league never overwrites them.
        </p>
      </header>

      {message && <div className={`notice ${message.kind === 'error' ? 'error' : 'ok'}`}>{message.text}</div>}

      <div className="row" style={{ marginBottom: 14 }}>
        <select
          value={teamFilter}
          onChange={(e) => setTeamFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
          aria-label="Filter by team"
        >
          <option value="all">All teams ({players.length} players)</option>
          {teams.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <button className="btn" onClick={() => void prefillFromLastSeason()} disabled={busy}>
          Fill from previous season
        </button>
        <button className="btn primary" onClick={() => void saveAll()} disabled={busy || dirtyPlayers.length === 0}>
          Save {dirtyPlayers.length > 0 ? `${dirtyPlayers.length} changed` : 'changes'}
        </button>
        <button className="btn" onClick={() => void load()} disabled={busy}>
          Discard edits
        </button>
      </div>

      <div className="table-scroll">
        <table className="form-table">
          <thead>
            <tr>
              <th className="left" rowSpan={2}>
                Player
              </th>
              <th className="left" rowSpan={2}>
                Source
              </th>
              <th colSpan={4}>Singles</th>
              <th colSpan={4}>Doubles</th>
              <th rowSpan={2}>Average</th>
              <th colSpan={4}>Scores</th>
              <th className="left" rowSpan={2}>
                Note
              </th>
              <th rowSpan={2} />
            </tr>
            <tr>
              {NUMBER_COLUMNS.filter((c) => c.group !== 'Scores').map((c) => (
                <th key={c.key}>{c.label}</th>
              ))}
              {NUMBER_COLUMNS.filter((c) => c.group === 'Scores').map((c) => (
                <th key={c.key}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((player) => {
              const draft = drafts.get(player.playerId) ?? EMPTY;
              const dirty = isDirty(player);
              return (
                <tr key={player.playerId} data-dirty={dirty}>
                  <td className="left">
                    {player.displayName}
                    <div className="muted" style={{ fontSize: '0.78rem' }}>
                      {player.teamName ?? '—'}
                    </div>
                  </td>
                  <td className="left">
                    <SourceBadge
                      source={player.statsSource}
                      carriedFrom={player.carriedFrom}
                      note={player.overrideNote}
                    />
                  </td>

                  {NUMBER_COLUMNS.filter((c) => c.group !== 'Scores').map((c) => (
                    <td key={c.key}>
                      <NumberCell
                        value={draft[c.key]}
                        onChange={(v) => patch(player.playerId, { [c.key]: v } as Partial<Draft>)}
                      />
                    </td>
                  ))}

                  <td>
                    <input
                      className="cell"
                      type="number"
                      min={0}
                      max={180}
                      step={0.01}
                      value={draft.average ?? ''}
                      placeholder="–"
                      onChange={(e) =>
                        patch(player.playerId, {
                          average: e.target.value === '' ? null : Number(e.target.value),
                        })
                      }
                      aria-label={`${player.displayName} average`}
                    />
                  </td>

                  {NUMBER_COLUMNS.filter((c) => c.group === 'Scores').map((c) => (
                    <td key={c.key}>
                      <NumberCell
                        value={draft[c.key]}
                        onChange={(v) => patch(player.playerId, { [c.key]: v } as Partial<Draft>)}
                      />
                    </td>
                  ))}

                  <td className="left">
                    <input
                      className="cell wide"
                      type="text"
                      value={draft.note ?? ''}
                      placeholder="optional"
                      onChange={(e) => patch(player.playerId, { note: e.target.value || null })}
                      aria-label={`${player.displayName} note`}
                    />
                  </td>

                  <td className="left" style={{ whiteSpace: 'nowrap' }}>
                    <button
                      className="btn"
                      style={{ padding: '3px 9px' }}
                      disabled={busy || !dirty}
                      onClick={() => void saveOne(player.playerId)}
                    >
                      Save
                    </button>
                    {player.hasOverride && (
                      <button
                        className="btn"
                        style={{ padding: '3px 9px', marginLeft: 4 }}
                        disabled={busy}
                        title="Discard the hand-entered figures and fall back to imported or previous-season data"
                        onClick={() => void clearOne(player.playerId)}
                      >
                        Clear
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {visible.length === 0 && <div className="empty-state">No players for this filter.</div>}
    </section>
  );
}

function NumberCell({ value, onChange }: { value: number; onChange: (value: number) => void }) {
  return (
    <input
      className="cell"
      type="number"
      min={0}
      step={1}
      value={value}
      onChange={(e) => onChange(e.target.value === '' ? 0 : Math.max(0, Math.floor(Number(e.target.value))))}
    />
  );
}
